'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ProviderProps } from '../types/types'

interface EntityContext {
    currentPage: string | null
    currentCandidateId: number | null
    currentJobId: number | null
    currentApplicationId: number | null
    recentSearches: string[]
    selectedCards: number[]
    availableFields?: Array<{ name: string; type: string; description: string; label?: string }>
    activeTab?: string | null
    activeFlyoutTab?: string | null
    timezone?: string | null
    displayedCandidates?: string
}

interface MarleeContextType {
    sessionId: string | null
    isPanelOpen: boolean
    entityContext: EntityContext

    setSessionId: (id: string | null) => void
    openPanel: () => void
    closePanel: () => void
    togglePanel: () => void
    updateEntityContext: (updates: Partial<EntityContext>) => void
    resetEntityContext: () => void
}

const DEFAULT_ENTITY_CONTEXT: EntityContext = {
    currentPage: null,
    currentCandidateId: null,
    currentJobId: null,
    currentApplicationId: null,
    recentSearches: [],
    selectedCards: [],
    availableFields: undefined,
    activeTab: null,
    activeFlyoutTab: null,
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
}

const MarleeContext = createContext<MarleeContextType>({
    sessionId: null,
    isPanelOpen: false,
    entityContext: DEFAULT_ENTITY_CONTEXT,
    setSessionId: () => {},
    openPanel: () => {},
    closePanel: () => {},
    togglePanel: () => {},
    updateEntityContext: () => {},
    resetEntityContext: () => {},
})

const useMarleeContext = () => useContext(MarleeContext)

const MarleeProvider: React.FC<ProviderProps> = ({ children }) => {
    const pathname = usePathname()
    const [sessionId, setSessionId] = useState<string | null>(null)
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    const [entityContext, setEntityContext] = useState<EntityContext>({
        ...DEFAULT_ENTITY_CONTEXT,
        currentPage: pathname,
    })

    useEffect(() => {
        const extractJobIdFromPath = (path: string | null): number | null => {
            if (!path) return null
            let match = path.match(/\/recruitment\/jobs\/(?:edit\/)?(\d+)/)
            if (match) {
                const id = parseInt(match[1], 10)
                return isNaN(id) ? null : id
            }
            match = path.match(/\/recruitment\/candidate\/new\/(\d+)/)
            if (match) {
                const id = parseInt(match[1], 10)
                return isNaN(id) ? null : id
            }
            return null
        }

        const resolvedJobId = extractJobIdFromPath(pathname)

        setEntityContext((prev) => ({
            ...prev,
            currentPage: pathname,
            currentCandidateId: null,
            currentApplicationId: null,
            currentJobId: resolvedJobId,
            selectedCards: [],
            activeTab: resolvedJobId ? prev.activeTab : null,
        }))
    }, [pathname])

    const openPanel = useCallback(() => setIsPanelOpen(true), [])
    const closePanel = useCallback(() => setIsPanelOpen(false), [])
    const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), [])

    const updateEntityContext = useCallback((updates: Partial<EntityContext>) => {
        setEntityContext((prev) => {
            let hasChanges = false
            for (const key of Object.keys(updates) as (keyof EntityContext)[]) {
                const prevVal = prev[key]
                const nextVal = updates[key]
                if (Array.isArray(prevVal) && Array.isArray(nextVal)) {
                    if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
                        hasChanges = true
                        break
                    }
                } else if (prevVal !== nextVal) {
                    hasChanges = true
                    break
                }
            }
            if (!hasChanges) return prev
            return { ...prev, ...updates }
        })
    }, [])

    const resetEntityContext = useCallback(() => {
        setEntityContext({ ...DEFAULT_ENTITY_CONTEXT, currentPage: pathname })
    }, [pathname])

    return (
        <MarleeContext.Provider
            value={{
                sessionId,
                isPanelOpen,
                entityContext,
                setSessionId,
                openPanel,
                closePanel,
                togglePanel,
                updateEntityContext,
                resetEntityContext,
            }}
        >
            {children}
        </MarleeContext.Provider>
    )
}

export { MarleeContext, MarleeProvider, useMarleeContext }
